import { Dispatch, SetStateAction } from 'react';
import { findUserByEmail, findGIDByUsername } from "../api/users";
import { useApiActivity } from "../contexts/ApiActivityContext";
import type { UserInfo } from "../types/user";

type FetchStatus = 'searching' | 'data' | 'error' | '';

interface UseSearchUserParams {
  setActiveStep: Dispatch<SetStateAction<number>>;
  setDiv2Visible: Dispatch<SetStateAction<boolean>>;
  setDiv2FetchStatus: Dispatch<SetStateAction<FetchStatus>>;
  setDiv2Title: Dispatch<SetStateAction<string>>;
  setUserInfo: Dispatch<SetStateAction<UserInfo | null>>;
  setDiv2Error: Dispatch<SetStateAction<string>>;
}

const useSearchUser = (
  setActiveStep: Dispatch<SetStateAction<number>>,
  setDiv2Visible: Dispatch<SetStateAction<boolean>>,
  setDiv2FetchStatus: Dispatch<SetStateAction<FetchStatus>>,
  setDiv2Title: Dispatch<SetStateAction<string>>,
  setUserInfo: Dispatch<SetStateAction<UserInfo | null>>,
  setDiv2Error: Dispatch<SetStateAction<string>>
): { searchUser: (enteredUsername: string, usernameType?: 'email' | 'retailGID') => Promise<void> } => {
  const { beginActivity, endActivity } = useApiActivity();

  const searchUser = async (enteredUsername: string, usernameType: 'email' | 'retailGID' = "email"): Promise<void> => {
    setActiveStep(2);
    setDiv2Visible(true);
    setDiv2FetchStatus("searching");
    setDiv2Title("Searching for the user...");

    let errorMsg: string | null = "";
    let userInfo: UserInfo | null = null;

    beginActivity("search");
    try {
      if (usernameType === "email") {
        ({ errorMsg, userInfo } = await findUserByEmail(enteredUsername));
      } else if (usernameType === "retailGID") {
        ({ errorMsg, userInfo } = await findGIDByUsername(enteredUsername));
      } else {
        setDiv2FetchStatus("error");
        setDiv2Title("Error!");
        setUserInfo(null);
        setDiv2Error(`Unexpected usernameType: ${usernameType}`);
        return;
      }

      if (userInfo) {
        setDiv2FetchStatus("data");
        setDiv2Title("");
        setUserInfo(userInfo);
        setDiv2Error("");
      } else if (errorMsg) {
        setDiv2FetchStatus("error");
        setDiv2Title("Error!");
        setUserInfo(null);
        setDiv2Error(errorMsg);
      } else {
        setDiv2FetchStatus("error");
        setDiv2Title("Error!");
        setUserInfo(null);
        setDiv2Error("Search completed without user data or an error message");
      }
    } finally {
      endActivity("search");
    }
  };

  return { searchUser };
};

export default useSearchUser;
